import {
  Box,
  Card,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import type {
  SalesHarvestContactRow,
  SalesHarvestOrg,
} from "@xop/dashboard-shared";

import { PageLoader } from "@/components/ui/page-loader";
import { Pagination } from "@/components/ui/pagination";

import { salesApi } from "../client.js";
import { SalesEmptyState, SalesErrorState } from "../components/SalesEmptyState.js";
import { verticalLabel } from "../components/VerticalBadge.js";
import { ContactDialog, type DialogContact } from "./ContactDialog.js";
import { ContactsTable, OrgsTable } from "./HarvestTables.js";
import { HarvesterWorldMap } from "./HarvesterWorldMap.js";
import { OrgDialog } from "./OrgDialog.js";

const PER_PAGE = 25;

type View = "orgs" | "contacts" | "map";

/**
 * Sales ▸ Contact Directory — a read-only window onto the contact-harvester
 * pool (the "contacts" track). Browse the blindly-harvested, de-duplicated
 * orgs + deep contacts, filter by industry, watch the counts grow. The agent
 * writes the pool; operators only read it here.
 *
 * Every row is industry-marked (a chip) — the harvester guarantees `vertical`
 * is never null, so the directory is always sliceable by industry.
 */
export function ContactDirectory(): JSX.Element {
  const [view, setView] = useState<View>("orgs");
  const [vertical, setVertical] = useState<string>("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [openOrgId, setOpenOrgId] = useState<string | null>(null);
  const [openContact, setOpenContact] = useState<DialogContact | null>(null);

  const stats = useQuery({
    queryKey: ["sales", "harvest", "stats"],
    queryFn: salesApi.getHarvestStats,
    refetchInterval: 30_000,
  });

  const orgs = useQuery({
    queryKey: ["sales", "harvest", "orgs", { vertical, q, page }],
    queryFn: () =>
      salesApi.getHarvestOrgs({
        ...(vertical ? { vertical } : {}),
        ...(q ? { q } : {}),
        page,
        per_page: PER_PAGE,
      }),
    enabled: view === "orgs",
    placeholderData: keepPreviousData,
  });

  const contacts = useQuery({
    queryKey: ["sales", "harvest", "contacts", { vertical, q, page }],
    queryFn: () =>
      salesApi.getHarvestContacts({
        ...(vertical ? { vertical } : {}),
        ...(q ? { q } : {}),
        page,
        per_page: PER_PAGE,
      }),
    enabled: view === "contacts",
    placeholderData: keepPreviousData,
  });

  const active = view === "orgs" ? orgs : contacts;
  const industries = Object.keys(stats.data?.orgs_by_vertical ?? {}).sort();

  const reset = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Harvester — contact directory
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Deep contacts the <strong>Harvester</strong> digs up across target
          industries — blindly collected, de-duplicated, industry-marked. When an
          org fits XOP it's promoted into a <strong>Lead</strong> (see the Leads
          tab). Switch to the <strong>Harvester</strong> (top-right) to fill it.
        </Typography>
      </Box>

      {/* Stat tiles */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <StatTile label="Organisations" value={stats.data?.total_orgs} />
        <StatTile label="Contacts" value={stats.data?.total_contacts} />
        <StatTile label="With email" value={stats.data?.contacts_with_email} />
        <StatTile label="With phone" value={stats.data?.contacts_with_phone} />
        <StatTile label="Leads created" value={stats.data?.leads_created} />
      </Stack>

      {/* Controls */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v: View | null) => v && reset(() => setView(v))}
          aria-label="directory view"
        >
          <ToggleButton value="orgs">Organisations</ToggleButton>
          <ToggleButton value="contacts">Contacts</ToggleButton>
          <ToggleButton value="map">Map</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          select
          size="small"
          label="Industry"
          value={vertical}
          onChange={(e) => reset(() => setVertical(e.target.value))}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All industries</MenuItem>
          {industries.map((slug) => (
            <MenuItem key={slug} value={slug}>
              {verticalLabel(slug)}
            </MenuItem>
          ))}
        </TextField>

        {view !== "map" && (
          <TextField
            size="small"
            label={view === "orgs" ? "Search organisations" : "Search people"}
            value={q}
            onChange={(e) => reset(() => setQ(e.target.value))}
            sx={{ minWidth: 240 }}
          />
        )}
      </Stack>

      {view === "map" ? (
        <HarvesterWorldMap
          {...(vertical ? { vertical } : {})}
          onOpenOrg={(id) => setOpenOrgId(id)}
        />
      ) : active.isLoading && !active.data ? (
        <PageLoader />
      ) : active.isError ? (
        <SalesErrorState error={active.error} />
      ) : active.data && active.data.total === 0 ? (
        <SalesEmptyState
          title="No contacts harvested yet"
          description="When the Harvester is running it enumerates facilities in the target industries and digs out named people. They'll appear here."
        />
      ) : (
        <Card variant="outlined" sx={{ overflow: "hidden" }}>
          {view === "orgs" ? (
            <OrgsTable
              items={(orgs.data?.items ?? []) as SalesHarvestOrg[]}
              onOpen={(id) => setOpenOrgId(id)}
            />
          ) : (
            <ContactsTable
              items={(contacts.data?.items ?? []) as SalesHarvestContactRow[]}
              onOpen={(c) => setOpenContact(c)}
            />
          )}
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
            <Pagination
              page={page}
              perPage={PER_PAGE}
              totalCount={active.data?.total ?? 0}
              onPageChange={setPage}
              entityNoun={view === "orgs" ? "organisations" : "contacts"}
            />
          </Box>
        </Card>
      )}

      <OrgDialog
        orgId={openOrgId}
        onClose={() => setOpenOrgId(null)}
        onOpenContact={(c) => setOpenContact(c)}
      />
      <ContactDialog
        contact={openContact}
        verifierEnabled={stats.data?.verifier_enabled ?? false}
        onClose={() => setOpenContact(null)}
      />
    </Box>
  );
}

function StatTile({ label, value }: { label: string; value: number | undefined }): JSX.Element {
  return (
    <Card variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 140 }}>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {value?.toLocaleString() ?? "—"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Card>
  );
}
